import { ListSubheader } from '@material-ui/core'
import { IconButton, InformationCircleOutlinedIcon, MenuItem, Select, styled, Tooltip, Typography } from 'opensolar-ui'
import {
  buildReleasePromoPreview,
  sanitizePromoElementsForPreview,
} from 'persistentContent/promoDialog/buildReleasePromoDialogs'
import { PromoDialogView } from 'persistentContent/promoDialog/PromoDialogView'
import type { ReleasePromoPreviewKind } from 'persistentContent/promoDialog/releasePromoDialogTypes'
import { OpenSolarVersionInfo } from 'persistentContent/promoDialog/useOpenSolarVersionStructs'
import { useTranslate } from 'ra-core'
import { useState } from 'react'
import { doNotTranslate } from 'util/misc'

const PREVIEW_TOOLTIP = doNotTranslate(
  "Select a preview to display the current form data. You can use the browser's device toolbar to check the mobile layout. And don't forget to click Save to preserve edits."
)

const TitleRow = styled('div', { name: 'OpenSolarVersionStructPreviewTitleRow' })({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  marginBottom: 8,
})

const PreviewTitle = styled(Typography, { name: 'OpenSolarVersionStructPreviewTitle' })({
  marginBottom: 0,
})

const PreviewInfoIcon = styled(InformationCircleOutlinedIcon, { name: 'OpenSolarVersionStructPreviewInfoIcon' })(
  ({ theme }) => ({
    fontSize: 18,
    color: theme.palette.grey[600],
  })
)

const PreviewSelect = styled(Select, { name: 'OpenSolarVersionStructPreviewSelect' })({
  width: '100%',
})

const StyledListSubheader = styled(ListSubheader, { name: 'StyledListSubheader' })({
  fontWeight: 600,
})

const StyledMenuItem = styled(MenuItem, { name: 'StyledMenuItem' })({
  marginLeft: 15,
})

/**
 * Section titles mirror Staff JSON schema field titles in
 * `django/osapi/struct/schemas/opensolar_version.py` (opensolar_version_json_schema).
 * Sub-labels name the progress/success variant shown for that form area.
 */
type PreviewSection = {
  /** Same wording as the corresponding accordion/group area in the struct form */
  formSectionTitle: string
  rows: { kind: ReleasePromoPreviewKind; label: string }[]
}

const PREVIEW_SECTIONS: PreviewSection[] = [
  {
    formSectionTitle: 'Show warning 2hrs before',
    rows: [{ kind: 'warning_2hr', label: '2 hours before release' }],
  },
  {
    formSectionTitle: 'Show warning 5mins before',
    rows: [{ kind: 'warning_5m', label: '5 minutes before release' }],
  },
  {
    formSectionTitle: 'Show promo during release',
    rows: [
      { kind: 'progress_in_progress_pro', label: 'OpenSolar Pro — update in progress' },
      { kind: 'progress_complete_pro', label: 'OpenSolar Pro — update complete' },
      { kind: 'progress_in_progress_customer', label: 'Customer — update in progress' },
      { kind: 'progress_complete_customer', label: 'Customer — update complete' },
    ],
  },
  {
    formSectionTitle: 'Show promo after release',
    rows: [{ kind: 'success', label: 'OpenSolar Pro' }],
  },
  {
    formSectionTitle: 'Show promo after release for OS Lite',
    rows: [{ kind: 'success_lite', label: 'OpenSolar Lite' }],
  },
]

const PREVIEW_KIND_VALUES = new Set<ReleasePromoPreviewKind>(
  PREVIEW_SECTIONS.flatMap((section) => section.rows.map((row) => row.kind))
)

type Props = {
  data: OpenSolarVersionInfo
}

export const OpenSolarVersionStructPreview = ({ data }: Props) => {
  const translate = useTranslate()
  const [previewKind, setPreviewKind] = useState<ReleasePromoPreviewKind | null>(null)

  const preview = previewKind ? buildReleasePromoPreview(previewKind, data, translate) : null
  const elements = preview ? sanitizePromoElementsForPreview(preview.elements) : undefined
  const blocker = preview?.dialogType === 'blocker'

  return (
    <>
      <TitleRow>
        <PreviewTitle textVariant="subtitle2">{doNotTranslate('Preview release modals')}</PreviewTitle>
        <Tooltip title={PREVIEW_TOOLTIP}>
          <IconButton size="small" aria-label={PREVIEW_TOOLTIP} style={{ padding: 2, margin: -2 }}>
            <PreviewInfoIcon />
          </IconButton>
        </Tooltip>
      </TitleRow>
      <PreviewSelect
        displayEmpty
        value={previewKind ?? ''}
        onChange={(e) => {
          const v = e.target.value as string
          if (v === '' || v == null) {
            setPreviewKind(null)
            return
          }
          if (PREVIEW_KIND_VALUES.has(v as ReleasePromoPreviewKind)) {
            setPreviewKind(v as ReleasePromoPreviewKind)
          } else {
            setPreviewKind(null)
          }
        }}
      >
        <MenuItem value="">
          <em>{doNotTranslate('Select a preview…')}</em>
        </MenuItem>
        {PREVIEW_SECTIONS.flatMap((section) => [
          <StyledListSubheader
            key={`sub-${section.formSectionTitle}`}
            disableSticky
            onMouseDown={(e) => {
              // Keep section titles from activating Select / firing onChange with invalid values (empty modal).
              e.preventDefault()
              e.stopPropagation()
            }}
          >
            {doNotTranslate(section.formSectionTitle)}
          </StyledListSubheader>,
          ...section.rows.map(({ kind, label }) => (
            <StyledMenuItem key={kind} value={kind}>
              {doNotTranslate(`Preview: ${label}`)}
            </StyledMenuItem>
          )),
        ])}
      </PreviewSelect>
      <PromoDialogView
        elements={elements}
        open={previewKind !== null}
        backEnabled={false}
        nextEnabled={true}
        onCloseComplete={() => setPreviewKind(null)}
        showActionsBar={!blocker}
        showPagination={!blocker}
        totalPages={1}
        currentPage={0}
        nextWillClose={true}
        onBack={() => {}}
        onNext={() => setPreviewKind(null)}
        onBackdropClick={() => setPreviewKind(null)}
      />
    </>
  )
}
